/**
 * Subscription Store - Subscription and usage state management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  SubscriptionState,
  SubscriptionActions,
  SubscriptionTier,
  FeatureKey,
  LocalizedPrice,
} from '@/types/subscription';
import { supabase, isSupabaseConfigured } from '@/services/supabase';
import {
  SUBSCRIPTION_TIERS,
  SUPPORTED_CURRENCIES,
  formatPrice,
  fetchExchangeRates,
} from '@/subscription/config';
import { useAuthStore } from './useAuthStore';

type SubscriptionStore = SubscriptionState & SubscriptionActions;

export const useSubscriptionStore = create<SubscriptionStore>()(
  persist(
    (set, get) => ({
      // State
      tier: 'free',
      subscription: null,
      apiCallsUsed: 0,
      apiCallsLimit: SUBSCRIPTION_TIERS.free.features.apiCallsPerMonth,
      currency: 'USD',
      exchangeRates: {},
      isLoading: false,
      error: null,

      // Actions
      fetchSubscription: async () => {
        // Re-fetch profile from Supabase to get latest subscription_tier
        await useAuthStore.getState().fetchProfile?.();
        const profile = useAuthStore.getState().profile;
        if (!profile) {
          // Cannot fetch profile — do NOT downgrade existing tier.
          // The user may have a valid 'pro' tier cached from a previous session.
          // Silently bail out rather than resetting to free.
          set({ isLoading: false });
          return;
        }

        set({ isLoading: true });

        try {
          // Get subscription tier from profile (now fresh from Supabase)
          const tier = profile.subscription_tier || 'free';
          const apiCallsUsed = profile.api_calls_this_month || 0;
          const apiCallsLimit = SUBSCRIPTION_TIERS[tier].features.apiCallsPerMonth;

          // Fetch detailed subscription if exists
          let subscription = null;
          if (isSupabaseConfigured && profile.stripe_customer_id) {
            const { data } = await supabase
              .from('subscriptions')
              .select('*')
              .eq('user_id', profile.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .single();

            subscription = data;
          }

          // Never silently downgrade from 'pro' → 'free'.
          // Only accept a downgrade if there's an explicit canceled subscription record.
          const currentTier = get().tier;
          const isExplicitDowngrade = tier === 'free' && currentTier === 'pro';
          const hasCanceledProof = subscription?.status === 'canceled' || subscription?.status === 'past_due';
          const resolvedTier = (isExplicitDowngrade && !hasCanceledProof) ? currentTier : tier;

          set({
            tier: resolvedTier,
            subscription,
            apiCallsUsed,
            apiCallsLimit,
            currency: profile.preferred_currency || 'USD',
            isLoading: false,
          });
        } catch (error) {
          console.error('Error fetching subscription:', error);
          set({ isLoading: false, error: 'Failed to fetch subscription' });
        }
      },

      createCheckoutSession: async (priceId: string) => {
        let { profile, user } = useAuthStore.getState();

        // If user/profile not loaded yet, refresh session and wait
        if (!profile && !user) {
          await useAuthStore.getState().refreshSession();
          ({ profile, user } = useAuthStore.getState());
        }

        // Use profile if available, fall back to auth user
        const userId = profile?.id || user?.id;
        const email = profile?.email || (user as any)?.email;

        if (!userId || !email) {
          set({ error: 'Please sign in to start your trial' });
          return null;
        }

        set({ isLoading: true, error: null });

        try {
          const response = await fetch('/api/create-checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              priceId,
              userId,
              email,
              successUrl: `${window.location.origin}/billing?success=true&session_id={CHECKOUT_SESSION_ID}`,
              cancelUrl: `${window.location.origin}/pricing?canceled=true`,
            }),
          });

          const data = await response.json();

          if (data.error) {
            set({ isLoading: false, error: data.error });
            return null;
          }

          set({ isLoading: false });
          return data.url;
        } catch (error) {
          console.error('Error creating checkout:', error);
          set({ isLoading: false, error: 'Failed to create checkout session' });
          return null;
        }
      },

      cancelSubscription: async () => {
        const { subscription } = get();
        if (!subscription?.stripe_subscription_id) {
          return { error: 'No active subscription' };
        }

        set({ isLoading: true, error: null });

        try {
          const response = await fetch('/api/cancel-subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              subscriptionId: subscription.stripe_subscription_id,
            }),
          });

          const data = await response.json();

          if (data.error) {
            set({ isLoading: false, error: data.error });
            return { error: data.error };
          }

          // Refresh subscription status
          await get().fetchSubscription();
          set({ isLoading: false });
          return {};
        } catch (error) {
          console.error('Error canceling subscription:', error);
          set({ isLoading: false, error: 'Failed to cancel subscription' });
          return { error: 'Failed to cancel subscription' };
        }
      },

      trackApiCall: async (endpoint: string) => {
        const { apiCallsUsed, apiCallsLimit } = get();
        const profile = useAuthStore.getState().profile;

        // Check if limit reached
        if (apiCallsUsed >= apiCallsLimit) {
          return false;
        }

        // Increment local count
        set({ apiCallsUsed: apiCallsUsed + 1 });

        // Update in database if authenticated
        if (profile && isSupabaseConfigured) {
          await supabase
            .from('profiles')
            .update({ api_calls_this_month: apiCallsUsed + 1 })
            .eq('id', profile.id);

          // Log the API call
          await supabase.from('api_usage').insert([
            {
              user_id: profile.id,
              endpoint,
              timestamp: new Date().toISOString(),
            },
          ]);
        }

        return true;
      },

      setCurrency: (currency: string) => {
        set({ currency });

        // Update in profile if authenticated
        const profile = useAuthStore.getState().profile;
        if (profile && isSupabaseConfigured) {
          supabase
            .from('profiles')
            .update({ preferred_currency: currency })
            .eq('id', profile.id);
        }
      },

      fetchExchangeRates: async () => {
        try {
          const rates = await fetchExchangeRates();
          set({ exchangeRates: rates });
        } catch (error) {
          console.error('Error fetching exchange rates:', error);
        }
      },

      canAccessFeature: (feature: FeatureKey) => {
        const { tier, subscription } = get();

        // Check if user has active subscription (includes 'trialing' status from Stripe)
        if (subscription?.status === 'trialing' || subscription?.status === 'active') {
          const subTier = subscription.tier || tier;
          const subTierConfig = SUBSCRIPTION_TIERS[subTier];
          const subFeatureValue = subTierConfig.features[feature];
          if (typeof subFeatureValue === 'boolean') return subFeatureValue;
          return true;
        }

        const tierConfig = SUBSCRIPTION_TIERS[tier];
        const featureValue = tierConfig.features[feature];

        // For boolean features
        if (typeof featureValue === 'boolean') {
          return featureValue;
        }

        // For non-boolean features, always true (but may be limited)
        return true;
      },

      getLocalizedPrice: (usdAmount: number): LocalizedPrice => {
        const { currency } = get();
        const currencyConfig = SUPPORTED_CURRENCIES[currency] || SUPPORTED_CURRENCIES.USD;
        const result = formatPrice(usdAmount, currency);

        return {
          currency,
          symbol: currencyConfig.symbol,
          amount: result.amount,
          formatted: result.formatted,
          usdAmount,
        };
      },
    }),
    {
      name: 'blurp-subscription-store',
      partialize: (state) => ({
        tier: state.tier,
        subscription: state.subscription,
        currency: state.currency,
        exchangeRates: state.exchangeRates,
      }),
    }
  )
);

// Selectors
export const selectTier = (state: SubscriptionStore) => state.tier;
export const selectSubscription = (state: SubscriptionStore) => state.subscription;
export const selectApiUsage = (state: SubscriptionStore) => ({
  used: state.apiCallsUsed,
  limit: state.apiCallsLimit,
  percentage: Math.round((state.apiCallsUsed / state.apiCallsLimit) * 100),
});
export const selectCurrency = (state: SubscriptionStore) => state.currency;
export const selectCanAccessFeature = (feature: FeatureKey) => (state: SubscriptionStore) =>
  state.canAccessFeature(feature);

// Hook for feature access check with upgrade prompt
export function useFeatureAccess(feature: FeatureKey) {
  const canAccess = useSubscriptionStore((state) => state.canAccessFeature(feature));
  const tier = useSubscriptionStore((state) => state.tier);

  return {
    hasAccess: canAccess,
    currentTier: tier,
    requiredTier: getRequiredTierForFeature(feature),
  };
}

function getRequiredTierForFeature(feature: FeatureKey): SubscriptionTier {
  if (SUBSCRIPTION_TIERS.free.features[feature]) return 'free';
  if (SUBSCRIPTION_TIERS.pro.features[feature]) return 'pro';
  return 'enterprise';
}

